import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSelectedChipId } from '../../state/aiSidekickSlice';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import InputBar from '../InputBar';
import SuggestionChip from '../SuggestionChip';
import SuggestionChipSkeleton from '../SuggestionChipSkeleton';
import { DOCK_LOADING_CHIP_COUNT } from './constants';
import useStyles from './SidekickDock.styles';
import type { ExpandedCardProps } from './types';
import { useDockConsentEntry } from './useDockConsentEntry';

/**
 * Body of the expanded dock for accounts with an active telematics
 * plan: assistant pitch, suggestion chips, and a composer.
 *
 * Both submission paths hand off to the drawer rather than answering
 * in place — the dock has no transcript, so the conversation has to
 * continue where the messages can be read. That handoff runs through
 * the legal-disclaimer gate, so neither path submits until consent is
 * settled.
 */
function ExpandedCard({
  chips,
  isChipsLoading = false,
  isTelematicsPlanLoading = false,
}: ExpandedCardProps) {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const selectedChipId = useSelector(selectSelectedChipId);
  const { handleChipClick, handleSubmit, isConsentCheckLoading } =
    useDockConsentEntry();
  // An unsettled plan check may still resolve to the upsell, so the
  // card cannot offer chips or take a question against it yet.
  const isEntryPending = isConsentCheckLoading || isTelematicsPlanLoading;
  const showLoadingChips = isChipsLoading || isEntryPending;

  const skeletonKeys = useMemo(
    () =>
      Array.from(
        { length: DOCK_LOADING_CHIP_COUNT },
        (_, index) => `dock-chip-skeleton-${index}`
      ),
    []
  );

  return (
    <>
      <Box className={classes.cardBody}>
        <Typography className={classes.title} component="h2">
          {t('panel.title')}
        </Typography>
        <Typography className={classes.subtitle} component="p">
          {t('dock.subtitle')}
        </Typography>
        <Box className={classes.chipsColumn} aria-busy={showLoadingChips}>
          {showLoadingChips
            ? skeletonKeys.map((key) => <SuggestionChipSkeleton key={key} />)
            : chips.map((chip, index) => {
                const label =
                  chip.label !== undefined
                    ? chip.label
                    : t(chip.translationKey);
                return (
                  <SuggestionChip
                    key={chip.id}
                    id={chip.id}
                    label={label}
                    selected={selectedChipId === chip.id}
                    onClick={() =>
                      handleChipClick(chip.id, label, {
                        chipPosition: index + 1,
                        chipCount: chips.length,
                      })
                    }
                  />
                );
              })}
        </Box>
      </Box>
      <Box className={classes.cardFooter}>
        {/* A submission while the entry checks are in flight is a
        no-op the composer cannot see, and it would clear the box on
        the way out. Holding the send here keeps the question. */}
        <InputBar
          onSubmit={handleSubmit}
          isStreaming={isEntryPending}
          isM1Enabled
          isInputFocusEnabled={false}
          testId="ai-sidekick-dock-input"
        />
        <Typography className={classes.disclaimer} component="p">
          {t('dock.disclaimer')}
        </Typography>
      </Box>
    </>
  );
}

export default ExpandedCard;
